import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

// Human word for each star value, shared by the review panels so ratings read
// the same everywhere ("5 · Excellent").
export const RATING_WORDS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Okay',
  4: 'Good',
  5: 'Excellent',
};

// A row of stars. Tappable when not readonly (emits valueChange); otherwise a
// compact read-only display that also shows half stars, so a fractional value
// (e.g. a 9/10 VRBO score shown as 4.5) doesn't round up to a perfect row. Kept
// dependency-free so both review panels — and anything else — can reuse it.
@Component({
  selector: 'star-rating',
  templateUrl: './star-rating.component.html',
  styleUrls: ['./star-rating.component.scss'],
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class StarRatingComponent implements OnChanges {
  private static readonly FILLED_SRC = 'assets/svg/star-yellow.svg';
  private static readonly HALF_SRC = 'assets/svg/star-yellow-half.svg';
  private static readonly EMPTY_SRC = 'assets/svg/star-outline.svg';

  @Input() value = 0;
  @Input() max = 5;
  @Input() readonly = false;
  // 'large' = tappable input size, 'small' = inline read-only size.
  @Input() size: 'small' | 'large' = 'large';

  @Output() valueChange = new EventEmitter<number>();

  // Each star's position and asset, precomputed so the template stays function-free.
  stars: { value: number; src: string }[] = [];

  ngOnChanges(): void {
    this.buildStars();
  }

  select(star: number): void {
    if (this.readonly) {
      return;
    }
    this.value = star;
    this.buildStars();
    this.valueChange.emit(star);
  }

  private buildStars(): void {
    // Nearest half star: 4.5 stays 4.5, 4.3 shows 4.5, 4.2 shows 4.
    const shown = Math.round((this.value || 0) * 2) / 2;
    this.stars = Array.from({ length: this.max }, (_, i) => {
      const star = i + 1;
      let src = StarRatingComponent.EMPTY_SRC;
      if (star <= shown) {
        src = StarRatingComponent.FILLED_SRC;
      } else if (star - 0.5 === shown) {
        src = StarRatingComponent.HALF_SRC;
      }
      return { value: star, src };
    });
  }
}
